// Format scan summary object to readable string
export const formatScanSummary = (summary) => {
  if (!summary || typeof summary !== 'object') return summary || '-';

  const parts = [];

  if (summary.added !== undefined) {
    parts.push(`${summary.added} added`);
  }
  if (summary.updated !== undefined) {
    parts.push(`${summary.updated} updated`);
  }
  if (summary.removed !== undefined) {
    parts.push(`${summary.removed} removed`);
  }
  if (summary.unchanged !== undefined) {
    parts.push(`${summary.unchanged} unchanged`);
  }

  return parts.length > 0 ? parts.join(', ') : '-';
};

// Get status class based on status value
export const getStatusClass = (status, styles) => {
  const normalizedStatus = status?.toUpperCase();

  switch (normalizedStatus) {
    case 'CLOSE':
    case 'ERROR':
    case 'ABORTED':
    case 'FAILED':
      return styles.statusError;
    case 'PARTIALSUCCESS':
      return styles.statusWarning;
    case 'IDLE':
      return styles.statusIdle;
    default: // SUCCESS / RUNNING / COMPLETED / IMPORTING
      return styles.statusActive;
  }
};

export const handleBack = (history) => {
  // Go back to the agentScanners list by removing the scanner ID from the path
  const currentPath = history.location.pathname;
  // Remove trailing slash if present, then remove the last segment (scanner ID)
  const pathWithoutTrailingSlash = currentPath.replace(/\/$/, '');
  const basePath = pathWithoutTrailingSlash.replace(/\/[^/]+$/, '');
  // Ensure the base path ends with / for consistency
  const finalPath = basePath.endsWith('/') ? basePath : `${basePath}/`;

  history.push(finalPath);
};

export const parseParameters = (parameters) => {
  try {
    if (!parameters) return null;
    // Backend can send: object directly, JSON string, or double-serialized JSON string
    if (typeof parameters === 'object') return parameters;
    const parsed =
      typeof parameters === 'string' ? JSON.parse(parameters) : parameters;

    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
  } catch (e) {
    return null;
  }
};

export const parseCronSchedule = (cronExpression) => {
  if (!cronExpression) return 'Not scheduled';

  try {
    // Cron format: seconds minutes hours day month weekday
    // e.g., "0 0 2 * * ?" = At 2:00 AM every day
    const parts = cronExpression.trim().split(/\s+/);

    if (parts.length !== 6) return cronExpression;

    const [seconds, minutes, hours, dayOfMonth, month, dayOfWeek] = parts;

    // Check for common patterns
    // Every X hours: "0 0 */X * * ?"
    if (
      hours.startsWith('*/') &&
      minutes === '0' &&
      seconds === '0' &&
      dayOfMonth === '*' &&
      month === '*'
    ) {
      const hourInterval = parseInt(hours.substring(2), 10);

      return `Every ${hourInterval} hour${hourInterval > 1 ? 's' : ''}`;
    }

    // Every X minutes: "0 */X * * * ?"
    if (
      minutes.startsWith('*/') &&
      hours === '*' &&
      dayOfMonth === '*' &&
      month === '*'
    ) {
      const minuteInterval = parseInt(minutes.substring(2), 10);

      return `Every ${minuteInterval} minute${minuteInterval > 1 ? 's' : ''}`;
    }

    // Daily at specific time: "0 M H * * ?"
    if (
      dayOfMonth === '*' &&
      month === '*' &&
      (dayOfWeek === '?' || dayOfWeek === '*')
    ) {
      const h = parseInt(hours, 10);
      const m = parseInt(minutes, 10);
      const period = h >= 12 ? 'PM' : 'AM';
      let displayHour = h;

      if (h === 0) {
        displayHour = 12;
      } else if (h > 12) {
        displayHour = h - 12;
      }
      const displayMinute = m.toString().padStart(2, '0');

      return `Daily at ${displayHour}:${displayMinute} ${period}`;
    }

    // Weekly on specific day: "0 M H ? * DOW"
    if (
      (dayOfMonth === '?' || dayOfMonth === '*') &&
      month === '*' &&
      dayOfWeek !== '?' &&
      dayOfWeek !== '*'
    ) {
      const daysMap = {
        SUN: 'Sunday',
        MON: 'Monday',
        TUE: 'Tuesday',
        WED: 'Wednesday',
        THU: 'Thursday',
        FRI: 'Friday',
        SAT: 'Saturday',
        0: 'Sunday',
        1: 'Monday',
        2: 'Tuesday',
        3: 'Wednesday',
        4: 'Thursday',
        5: 'Friday',
        6: 'Saturday'
      };
      const dayName = daysMap[dayOfWeek] || dayOfWeek;
      const h = parseInt(hours, 10);
      const m = parseInt(minutes, 10);
      const period = h >= 12 ? 'PM' : 'AM';
      let displayHour = h;

      if (h === 0) {
        displayHour = 12;
      } else if (h > 12) {
        displayHour = h - 12;
      }
      const displayMinute = m.toString().padStart(2, '0');

      return `Weekly on ${dayName} at ${displayHour}:${displayMinute} ${period}`;
    }

    // Monthly: "0 M H D * ?"
    if (
      dayOfMonth !== '*' &&
      dayOfMonth !== '?' &&
      month === '*' &&
      dayOfWeek === '?'
    ) {
      const day = parseInt(dayOfMonth, 10);
      const h = parseInt(hours, 10);
      const m = parseInt(minutes, 10);
      const period = h >= 12 ? 'PM' : 'AM';
      let displayHour = h;

      if (h === 0) {
        displayHour = 12;
      } else if (h > 12) {
        displayHour = h - 12;
      }
      const displayMinute = m.toString().padStart(2, '0');
      let daySuffix = 'th';

      if (day === 1) {
        daySuffix = 'st';
      } else if (day === 2) {
        daySuffix = 'nd';
      } else if (day === 3) {
        daySuffix = 'rd';
      }

      return `Monthly on the ${day}${daySuffix} at ${displayHour}:${displayMinute} ${period}`;
    }

    // If no pattern matches, return original
    return cronExpression;
  } catch (e) {
    return cronExpression;
  }
};

export const authMapper = (value) => {
  const authMethodMap = {
    // serviceAccount: 'Service Account',
    // apiKey: 'API Key',
    AWS_IAM: 'AWS IAM',
    Bearer: 'Service Account'
  };

  return authMethodMap[value] || value;
};

/**
 * Parse a JSON schedule object to readable string
 * @param {string|Object} schedule - JSON string or object with schedule info
 * @returns {string} Human readable schedule description
 */
export const parseScheduleJson = (schedule) => {
  if (!schedule) return 'Not scheduled';

  try {
    const parsed =
      typeof schedule === 'string' ? JSON.parse(schedule) : schedule;

    // Handle common JSON schedule formats
    if (parsed.frequency) {
      return parsed.frequency;
    }
    if (parsed.interval && parsed.unit) {
      return `Every ${parsed.interval} ${parsed.unit}`;
    }
    if (parsed.cron) {
      return parsed.cron;
    }

    // Return stringified if no known format
    return JSON.stringify(parsed);
  } catch (e) {
    return String(schedule);
  }
};

/**
 * Parse schedule (detects JSON or cron format)
 * @param {string|Object} schedule - Schedule in JSON or cron format
 * @returns {string} Human readable schedule description
 */
export const parseSchedule = (schedule) => {
  if (!schedule) {
    return parseCronSchedule(schedule);
  }

  // Try to detect if it's JSON
  const trimmedSchedule =
    typeof schedule === 'string' ? schedule.trim() : schedule;

  if (
    typeof trimmedSchedule === 'string' &&
    trimmedSchedule.startsWith('{') &&
    trimmedSchedule.endsWith('}')
  ) {
    try {
      JSON.parse(trimmedSchedule);

      return parseScheduleJson(trimmedSchedule);
    } catch {
      return parseCronSchedule(schedule);
    }
  }

  // If it's already an object, treat as JSON
  if (typeof schedule === 'object' && schedule !== null) {
    return parseScheduleJson(schedule);
  }

  // Otherwise, treat as cron expression
  return parseCronSchedule(schedule);
};

/**
 * Format status text with first letter uppercase and rest lowercase
 * @param {string} status - Status string (e.g., 'ACTIVE', 'running')
 * @param {string} fallback - Fallback value if status is falsy (default: '-')
 * @returns {string} Formatted status (e.g., 'Active', 'Running') or fallback
 */
export const formatStatusText = (status, fallback = '-') => {
  if (!status) return fallback;

  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

/**
 * Get stats from lastSuccessfulRunSummary (handles JSON string or object)
 * @param {string|Object} summary - Summary data as JSON string or object
 * @returns {Object|null} Stats object or null if parsing fails
 */
export const getStatsFromSummary = (summary) => {
  if (!summary) return null;

  try {
    const parsed = typeof summary === 'string' ? JSON.parse(summary) : summary;

    return parsed?.stats || parsed;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to parse lastSuccessfulRunSummary:', e);

    return null;
  }
};

export const formatRelativeTime = (dateString) => {
  if (!dateString) return 'Never';

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date - now; // Positive if future, negative if past
    const absDiffMs = Math.abs(diffMs);
    const absDiffSeconds = Math.floor(absDiffMs / 1000);
    const absDiffMinutes = Math.floor(absDiffSeconds / 60);
    const absDiffHours = Math.floor(absDiffMinutes / 60);
    const absDiffDays = Math.floor(absDiffHours / 24);
    const isFuture = diffMs > 0;

    // Handle very recent times (within 60 seconds)
    if (absDiffSeconds < 60) {
      return isFuture ? 'In a moment' : 'Just now';
    }

    // Handle minutes
    if (absDiffMinutes < 60) {
      return isFuture
        ? `In ${absDiffMinutes} min${absDiffMinutes > 1 ? 's' : ''}`
        : `${absDiffMinutes} min${absDiffMinutes > 1 ? 's' : ''} ago`;
    }

    // Handle hours
    if (absDiffHours < 24) {
      return isFuture
        ? `In ${absDiffHours} hr${absDiffHours > 1 ? 's' : ''}`
        : `${absDiffHours} hr${absDiffHours > 1 ? 's' : ''} ago`;
    }

    // Handle days (up to 7 days)
    if (absDiffDays < 7) {
      return isFuture
        ? `In ${absDiffDays} day${absDiffDays > 1 ? 's' : ''}`
        : `${absDiffDays} day${absDiffDays > 1 ? 's' : ''} ago`;
    }

    // For dates beyond 7 days, return the date string
    return date.toDateString();
  } catch (e) {
    return dateString;
  }
};
